import { useEffect, useState, useRef } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

/**
 * v2.33 — Floating scroll-progress chip for long-form pages (Home has 30+ rails).
 *
 * Shows a small bottom-right pill: "12 more rails ↓" with a thin progress arc.
 * Tapping the chip:
 *   - When < 90% scrolled: smooth-scrolls to bottom of page
 *   - When > 90%: smooth-scrolls back to top
 *
 * Counts the remaining `data-testid="row-*"` rails below the current viewport
 * so the label is always meaningful. Hides when fewer than 3 rails remain.
 */
export default function ScrollProgressChip() {
  const [percent, setPercent] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const totalRailsRef = useRef(0);

  useEffect(() => {
    let raf = 0;
    const compute = () => {
      raf = 0;
      const doc = document.documentElement;
      const scrollTop = window.scrollY || doc.scrollTop;
      const maxScroll = Math.max(1, doc.scrollHeight - window.innerHeight);
      const pct = Math.min(100, Math.round((scrollTop / maxScroll) * 100));
      setPercent(pct);

      // Count rails below the bottom of the viewport. We include BOTH
      // currently-mounted rails AND lazy-mount placeholders (so the count
      // is accurate before scroll triggers any mounts).
      const viewportBottom = scrollTop + window.innerHeight;
      const candidates = document.querySelectorAll(
        "[data-testid^='row-'], [data-testid^='lazy-']"
      );
      const seen = new Set();
      let below = 0;
      candidates.forEach((el) => {
        const tid = el.getAttribute("data-testid") || "";
        // Skip sub-control testids that share the row prefix
        if (
          tid.endsWith("-refresh") ||
          tid.endsWith("-viewall") ||
          tid.endsWith("-scroll-right") ||
          tid.endsWith("-scroll-left")
        )
          return;
        // Normalise: lazy-foo and row-foo refer to the same conceptual rail
        const key = tid.replace(/^lazy-/, "").replace(/^row-/, "");
        if (seen.has(key)) return;
        seen.add(key);
        const rect = el.getBoundingClientRect();
        const absTop = rect.top + scrollTop;
        if (absTop > viewportBottom) below += 1;
      });
      totalRailsRef.current = seen.size;
      setRemaining(below);
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(compute);
    };
    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    // Re-count after content lazy-mounts (every 1.5s up to 12s).
    const interval = setInterval(compute, 1500);
    setTimeout(() => clearInterval(interval), 12000);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      clearInterval(interval);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Hide chip when nearly nothing left below + at top
  if (remaining < 2 && percent < 5) return null;
  if (totalRailsRef.current < 5) return null;

  const atBottom = percent > 92;
  const handleClick = () => {
    if (atBottom) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      window.scrollTo({
        top: document.documentElement.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  return (
    <button
      onClick={handleClick}
      data-testid="scroll-progress-chip"
      aria-label={atBottom ? "Back to top" : "Jump to bottom"}
      className="fixed bottom-44 right-5 z-30 group flex items-center gap-2 px-3.5 py-2 rounded-full bg-black/85 border border-white/15 backdrop-blur-md shadow-lg shadow-black/40 hover:border-orange-400/60 hover:bg-black/95 transition-all"
    >
      {/* Progress arc */}
      <svg className="w-6 h-6 -rotate-90" viewBox="0 0 24 24">
        <circle
          cx="12"
          cy="12"
          r="9"
          fill="none"
          className="stroke-white/15"
          strokeWidth="2.5"
        />
        <circle
          cx="12"
          cy="12"
          r="9"
          fill="none"
          className="stroke-orange-400 group-hover:stroke-orange-300 transition-colors"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeDasharray={`${(percent / 100) * 56.5} 999`}
        />
      </svg>
      <span className="text-[11px] font-semibold tracking-wide text-white/85 group-hover:text-white">
        {atBottom ? (
          <>Top <ChevronUp className="inline w-3 h-3 -mt-0.5" /></>
        ) : remaining > 0 ? (
          <>{remaining} more <ChevronDown className="inline w-3 h-3 -mt-0.5" /></>
        ) : (
          <>{percent}%</>
        )}
      </span>
    </button>
  );
}
